import React, { CSSProperties, useCallback, useMemo } from "react";

import {
  Checkbox,
  Icon,
  Label,
  Popup,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import moment from "moment";

import EditorColumn from "react-lib/appcon/common/EditorColumn";
import LoadingSwitch from "react-lib/appcon/common/LoadingSwitch";

import { RehabPlanItemDetailType, RehabPlanType } from "react-lib/apps/HISV3/RHB/RHBInterface";

import { formatDate, formatDatetime, formatISOToThaiDate } from "react-lib/utils/dateUtils";

import "./TableTreatment.scss";
import "./TableTreatmentPlan.scss";

type TableTreatmentPlanProps = {
  checkedPerformList: { no: number; plan_item: number }[];
  data?: RehabPlanType[];
  hideColumns?: ColumnKeys[];
  hideEditDepartment?: boolean;
  isIncludePerformed: boolean;
  selectedMode: "" | "APPOINTMENT" | "PERFORM" | "PRINT";
  showOnlyCancelledPlan: boolean;
  buttonLoadCheck: any;
  onCancelPlan: (data: Record<string, any>) => void;
  onChangeCheckPerform: (data: Record<string, any>) => void;
  onChangeIsContinue: (isChecked: boolean, params: { id: number; btnAction: string }) => void;
  onChangeRepetition: (
    value: string,
    params: { btnAction: string; data: Record<string, any> }
  ) => void;
  onEditDepartment: (data: Record<string, any>) => void;
  onOpenGallery: (images: any[]) => void;
  onOpenHistory: (data: Record<string, any>) => void;
};

type Styles = Record<"switch_style" | "to_department", CSSProperties>;

const styles: Styles = {
  switch_style: { margin: "0 -8px 0 -2px", transform: "scale(0.65)" },
  to_department: {
    columnGap: "0.25rem",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    marginTop: "0.25rem",
  },
};

const IMAGES = {
  gallery: "/static/images/rehab/gallery.png",
  gallery_grey: "/static/images/rehab/gallery-grey.png",
  goal: "/static/images/rehab/goal.png",
  precaution: "/static/images/rehab/precaution.png",
  problem: "/static/images/rehab/problem.png",
};

const TABLE_TREATMENT = "TableTreatment";

export type ColumnKeys = (typeof COLUMNS)[number]["key"];

const COLUMNS = [
  { key: "create_plan", label: "Created plan / Careprovider", width: 2 },
  { key: "diagnosis", label: "Diagnosis", width: 3 },
  { key: "treatmentDetail", label: "Treatment & Detail", width: 5 },
  { key: "repetition", label: "Repetition (Remaining/Total)", width: 1 },
  { key: "appointmentList", label: "Appointment/Performed", width: 1 },
] as const;

const TableTreatment = (props: TableTreatmentPlanProps) => {
  const handleChangeIsContinue = useCallback(
    (data: Record<string, any>) => (e: any, checked: boolean) => {
      props.onChangeIsContinue(checked, {
        id: data.id,
        btnAction: `${TABLE_TREATMENT}_IS_CONTINUE_${data.id}`,
      });
    },
    [props.onChangeIsContinue]
  );

  const getEnabledPerformIndex = useCallback(
    (appointments: Record<string, any>[]) => {
      // ตรวจสอบว่ามีการเลือก appointment ใดๆ แล้วหรือไม่
      const hasAnySelectedAppointment = appointments.some((appointment) => appointment.is_selected);

      // ค้นหาตำแหน่งของ appointment ถัดไปที่สามารถทำได้
      return appointments.findIndex((appointment) => {
        // ข้ามถ้าถูกเลือกแล้วหรือมีการเลือกอื่นแล้ว
        if (appointment.is_selected || hasAnySelectedAppointment) {
          return false;
        }

        // mode อื่นๆ: เอาที่ยังไม่มีวันที่ดำเนินการ
        const isAppointmentMode =
          props.selectedMode === "APPOINTMENT" && appointment.appointment_date;
        const hasValidDate = appointment.performed_date || isAppointmentMode;

        return !hasValidDate;
      });
    },
    [props.selectedMode]
  );

  // Flatten data structure
  const flattenedData = useMemo(() => {
    const result: (RehabPlanType & {
      appointment: Record<string, any>;
      item: RehabPlanItemDetailType;
      item_id: number;
      rehab_id: number | undefined;
    })[] = [];

    for (const rehab of props.data ?? []) {
      const filteredItems = rehab.items.filter((item) =>
        props.showOnlyCancelledPlan ? item.status === "X" : item.status !== "X"
      );

      for (const item of filteredItems) {
        const filteredAppointments = (item.appointments ?? []).filter(
          (appointment) => props.isIncludePerformed || !appointment.performed_date
        );

        if (filteredAppointments.length > 0) {
          for (const appointment of filteredAppointments) {
            result.push({
              ...rehab,
              appointment,
              item,
              item_id: item.id,
              rehab_id: rehab.id,
            });
          }
        } else if (item.status === "X") {
          result.push({
            ...rehab,
            appointment: [],
            item,
            item_id: item.id,
            rehab_id: rehab.id,
          });
        }
      }
    }

    return result;
  }, [props.data, props.isIncludePerformed, props.showOnlyCancelledPlan]);

  const renderTreatmentDetail = useCallback(
    (item: RehabPlanItemDetailType, rehab: RehabPlanType) => {
      const images = item.images ?? [];

      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 65px" }}>
          <div>
            <div>
              <strong>{item.name || ""}</strong>
            </div>
            <div>{item.detail}</div>
            <div style={{ marginTop: "0.5rem" }}>
              Frequency : {item.frequency} time(s)/ week , Duration {item.duration} week(s)
            </div>
            <div style={styles.to_department}>
              <div>To department : </div>
              <div>
                {item.to_department_for_physiotherapist_name}{" "}
                {!props.hideEditDepartment && !props.showOnlyCancelledPlan && (
                  <Icon
                    style={{ fontSize: "0.85rem", marginLeft: "0.5rem" }}
                    color="yellow"
                    name="edit outline"
                    link
                    onClick={() => {
                      props.onEditDepartment(item);
                    }}
                  />
                )}
              </div>
            </div>
            {rehab.encounter_type === "IPD" && (
              <div style={{ margin: "0.5rem 0 0 0.75rem" }}>
                <LoadingSwitch
                  checked={item.is_continue}
                  disabled={props.hideEditDepartment}
                  loaderStyle={{ marginLeft: item.is_continue ? "-10px" : "-22px" }}
                  loading={!!props.buttonLoadCheck?.[`${TABLE_TREATMENT}_IS_CONTINUE_${item.id}`]}
                  switchStyle={styles.switch_style}
                  onChange={handleChangeIsContinue(item)}
                  label={
                    <div style={{ fontSize: "0.85rem", fontWeight: "bold", lineHeight: 1.15 }}>
                      <div>Continue treatment</div>
                      <div>in current Encounter</div>
                    </div>
                  }
                />
              </div>
            )}
          </div>
          <div style={{ display: "flex" }}>
            <Popup
              disabled={!item.icd9cm}
              position="bottom center"
              content={
                <div>
                  [{item.icd9_code}] {item.medical_description}
                  <div
                    style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.35rem" }}
                  >
                    <Checkbox checked={item.icd9_type === "PRIMARY"} label="is primary" />
                  </div>
                </div>
              }
              trigger={
                <label
                  style={{
                    color: item.icd9cm ? "rgba(0, 114, 188, 1)" : "rgba(199, 199, 199, 1)",
                    fontWeight: "bold",
                    height: "fit-content",
                  }}
                >
                  ICD-9
                </label>
              }
            />
            <img
              style={{
                cursor: images.length > 0 ? "pointer" : "",
                height: "20px",
                marginLeft: "0.45rem",
              }}
              alt="gallery.icon"
              src={images.length > 0 ? IMAGES.gallery : IMAGES.gallery_grey}
              onClick={() => {
                if (images.length > 0) {
                  props.onOpenGallery(images);
                }
              }}
            />
            {!props.showOnlyCancelledPlan && (
              <div
                style={{
                  bottom: "0.15rem",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "0.5rem",
                }}
                onClick={() => {
                  props.onCancelPlan(item);
                }}
              >
                <Icon color="red" name="remove circle" link />
                <label style={{ color: "rgba(198, 0, 0, 1)", cursor: "pointer" }}>
                  Cancel Plan
                </label>
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      handleChangeIsContinue,
      props.buttonLoadCheck,
      props.hideEditDepartment,
      props.onCancelPlan,
      props.onEditDepartment,
      props.onOpenGallery,
    ]
  );

  const renderDiagnosis = useCallback(
    (rehab: RehabPlanType) => (
      <div>
        <div>
          {rehab.diagnosis?.principal && (
            <>
              <div>
                <strong>Principal Dx.</strong>
              </div>
              {`[${rehab.diagnosis.principal.icd10_code}] ${rehab.diagnosis.principal.icd10_term}`}
            </>
          )}
        </div>
        {!!rehab.diagnosis?.secondary?.[0]?.icd10_code && (
          <>
            <div style={{ marginTop: "0.35rem" }}>
              <strong>Secondary Dx.</strong>
            </div>
            {rehab.diagnosis.secondary.map((acc) => (
              <div key={acc.icd10_code}>{`[${acc.icd10_code}] ${acc.icd10_term}`}</div>
            ))}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.75rem" }}>
          {!!rehab.problem && (
            <Popup
              content={rehab.problem}
              position="bottom center"
              trigger={
                <img
                  style={{ height: "30px", marginLeft: "0.25rem" }}
                  alt="problem.icon"
                  src={IMAGES.problem}
                />
              }
            />
          )}
          {!!rehab.goal && (
            <Popup
              content={rehab.goal}
              position="bottom center"
              trigger={
                <img
                  style={{ height: "30px", marginLeft: "0.25rem" }}
                  alt="goal.icon"
                  src={IMAGES.goal}
                />
              }
            />
          )}
          {rehab.precaution.length > 0 && (
            <Popup
              content={rehab.precaution.join(", ")}
              position="bottom center"
              trigger={
                <img
                  style={{ height: "30px", marginLeft: "0.25rem" }}
                  alt="precaution.icon"
                  src={IMAGES.precaution}
                />
              }
            />
          )}
        </div>
      </div>
    ),
    []
  );

  const filteredColumns = useMemo(
    () => COLUMNS.filter((column) => !props.hideColumns?.includes(column.key)),
    [props.hideColumns]
  );

  return (
    <Table id="TableTreatment" celled compact structured>
      <TableHeader className="rehab-treatment">
        <TableRow>
          {filteredColumns.map((column) => (
            <TableHeaderCell key={column.key} textAlign="center" width={column.width}>
              {column.label}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        {flattenedData.length === 0 ? (
          <TableCell
            style={{ color: "grey" }}
            colSpan={filteredColumns.length.toString()}
            textAlign="center"
          >
            No rows found
          </TableCell>
        ) : null}

        {flattenedData.map((row, index) => {
          // Group rows by rehab plan
          const isFirstRowForRehab =
            index === 0 ||
            (props.showOnlyCancelledPlan
              ? flattenedData[index - 1].item_id !== row.item_id
              : flattenedData[index - 1].rehab_id !== row.rehab_id);
          const rowsForThisRehab = flattenedData.filter((d) =>
            props.showOnlyCancelledPlan ? d.item_id === row.item_id : d.rehab_id === row.rehab_id
          ).length;

          // Group rows by treatment item
          const isFirstRowForItem = index === 0 || flattenedData[index - 1].item_id !== row.item_id;
          const rowsForThisItem = flattenedData.filter((d) => d.item_id === row.item_id).length;

          const enabledPerformIndex = getEnabledPerformIndex(row.item.appointments ?? []);

          const enabledCheckbox =
            props.selectedMode === "PRINT"
              ? row.appointment.appointment_date
              : enabledPerformIndex === row.appointment.no - 1 && props.selectedMode;

          const isChecked = props.checkedPerformList.find(
            (perform) => perform.plan_item === row.item.id && row.appointment.no === perform.no
          );

          const isCancelledPlan =
            props.showOnlyCancelledPlan &&
            !row.appointment.performed_date &&
            !row.appointment.appointment_date;

          return (
            <TableRow key={index}>
              {/* Create Plan - merge for all appointments of this rehab plan */}
              {!props.hideColumns?.includes("create_plan") && isFirstRowForRehab && (
                <TableCell rowSpan={rowsForThisRehab} verticalAlign="top">
                  <div>
                    <div>{formatDatetime(row.created, true)}</div>
                    <div>{row.order_by_name}</div>
                    {!!row.order_receive_by_name && (
                      <>
                        <Label color="blue" basic>
                          Staff Recorded
                        </Label>
                        <div>{row.order_receive_by_name}</div>
                      </>
                    )}
                    {props.showOnlyCancelledPlan && (
                      <div style={{ marginTop: "0.25rem" }}>
                        <Label
                          style={{
                            backgroundColor: "#f5d0ce",
                            color: "#cf2727",
                            padding: "4px 4px",
                          }}
                          size="small"
                        >
                          CANCEL
                        </Label>
                        <label
                          style={{
                            color: "#8f8d8d",
                            fontSize: "10px",
                            fontWeight: "bold",
                            lineHeight: "0.8",
                            marginLeft: "0.35rem",
                          }}
                        >
                          {row.item.canceled_reason}
                        </label>
                        <div>
                          <label
                            style={{
                              fontSize: "10px",
                              fontStyle: "italic",
                              lineHeight: "0.8",
                            }}
                          >
                            By: {row.item.canceled_user_name} {formatISOToThaiDate(row.item.edited)}
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </TableCell>
              )}

              {/* Diagnosis - merge for all appointments of this rehab plan */}
              {!props.hideColumns?.includes("diagnosis") && isFirstRowForRehab && (
                <TableCell rowSpan={rowsForThisRehab} verticalAlign="top">
                  {renderDiagnosis(row)}
                </TableCell>
              )}

              {/* Treatment Detail - merge for all appointments of this item */}
              {!props.hideColumns?.includes("treatmentDetail") && isFirstRowForItem && (
                <TableCell
                  className="position-relative"
                  rowSpan={rowsForThisItem}
                  verticalAlign="top"
                >
                  {renderTreatmentDetail(row.item, row)}
                </TableCell>
              )}

              {/* Repetition - merge for all appointments of this item */}
              {!props.hideColumns?.includes("repetition") && isFirstRowForItem && (
                <TableCell
                  className="vertical-align-center"
                  rowSpan={rowsForThisItem}
                  textAlign="center"
                >
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ alignItems: "baseline", display: "flex" }}>
                      <div>{`${
                        row.item.total_for_physiotherapist - (row.item.performed_count ?? 0)
                      }`}</div>
                      <label style={{ margin: "0 0.1rem 0 0.25rem" }}> / </label>
                      {props.showOnlyCancelledPlan ? (
                        <div>{row.item.total_for_physiotherapist}</div>
                      ) : (
                        <EditorColumn
                          align="left"
                          inputMin={0}
                          inputStyle={{ width: "65px" }}
                          inputType="number"
                          triggerDimmerStyle={{ marginLeft: "0.25rem" }}
                          triggerStyle={{ marginLeft: "1rem" }}
                          value={row.item.total_for_physiotherapist}
                          editOnTrigger
                          inputNumber
                          selectOnFocus
                          valueOnFocus
                          onDataChanged={(value: string) => {
                            props.onChangeRepetition(value, {
                              btnAction: `${TABLE_TREATMENT}_EDIT_${row.item.id}`,
                              data: row.item,
                            });
                          }}
                          triggerLoading={
                            !!props.buttonLoadCheck?.[`${TABLE_TREATMENT}_EDIT_${row.item.id}`]
                          }
                        />
                      )}
                    </div>
                  </div>
                </TableCell>
              )}

              {/* Appointment List - individual row for each appointment */}
              {!props.hideColumns?.includes("appointmentList") && (
                <TableCell
                  className="vertical-align-center"
                  style={isCancelledPlan ? { backgroundColor: "#f3f3f3", padding: 0 } : {}}
                >
                  {isCancelledPlan ? (
                    <div>{"\u00A0"}</div>
                  ) : (
                    <div
                      className="--override-disabled --grey"
                      style={{
                        alignItems: "center",
                        display: "grid",
                        gridTemplateColumns: "20px 40px 25px 1fr",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        {row.appointment.performed_date ? (
                          <Icon
                            style={{ fontSize: "1.25rem" }}
                            color="blue"
                            name="folder outline"
                            link
                            onClick={() => {
                              props.onOpenHistory(row.appointment);
                            }}
                          />
                        ) : (
                          <Checkbox
                            checked={!!isChecked}
                            disabled={!enabledCheckbox}
                            onChange={(e, data) => {
                              props.onChangeCheckPerform({
                                checked: data.checked,
                                encounter: row.encounter,
                                no: row.appointment.no,
                                plan_item: row.item.id,
                                plan_item_detail: row.item,
                              });
                            }}
                          />
                        )}
                      </div>
                      <label style={{ margin: "0 0.75rem", minWidth: "max-content" }}>
                        {row.appointment.no} :{" "}
                      </label>
                      {row.appointment.appointment_date && (
                        <>
                          <div>
                            <Icon color="blue" name="calendar alternate outline" />
                          </div>
                          <div style={{ color: "rgba(0, 114, 188, 1)", fontSize: "0.85rem" }}>
                            {row.appointment.appointment_date}
                          </div>
                        </>
                      )}
                      {row.appointment.performed_date && (
                        <>
                          <div>
                            <Icon
                              style={{ fontSize: "1rem", marginTop: "3px" }}
                              color="green"
                              name="check circle"
                            />
                          </div>
                          <div
                            style={{
                              color: "rgba(27, 157, 44, 1)",
                              fontSize: "0.7rem",
                              lineHeight: 1.25,
                            }}
                          >
                            <div>Performed</div>
                            <div>{formatDate(moment(row.appointment.performed_date))}</div>
                          </div>
                        </>
                      )}
                      {row.appointment.is_selected && (
                        <>
                          <div />
                          <div
                            style={{
                              color: "rgba(0, 114, 188, 1)",
                              fontWeight: "bold",
                              marginLeft: "-25px",
                            }}
                          >
                            Selected
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

TableTreatment.displayName = "TableTreatment";

export default React.memo(TableTreatment);
